import { createContext, useCallback, useContext, useMemo } from 'react';
import { ProductSessionProvider, useProductSession } from '@xinosolutions/xs-auth-react';
import { baseUrl } from '../config/config';
import { createSitesProductApiConfig } from '../config/sitesProductApi';

/** Absolute API host (same as invokeApi). Same-origin /api only if API_BASE_URL is unset. */
const PRODUCT_API = createSitesProductApiConfig(baseUrl);

const ProductContext = createContext(undefined);

function normalizeUser(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const email = typeof raw.email === 'string' ? raw.email.trim() : '';
  if (!email) return null;

  const firstName = raw.first_name ?? raw.firstName ?? '';
  const lastName = raw.last_name ?? raw.lastName ?? '';
  const name =
    (typeof raw.name === 'string' && raw.name.trim()) ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    email;

  return {
    _id: raw._id ?? raw.id,
    id: raw._id ?? raw.id,
    name,
    email,
    first_name: firstName,
    last_name: lastName,
    apps: raw.apps,
    user_type: raw.user_type,
    role: raw.role,
  };
}

function ProductInner({ children }) {
  const { workspace, loading, ready, failureReason, refresh, clear } = useProductSession();

  const member = useMemo(() => {
    const raw =
      workspace?.client ?? workspace?.client_user ?? workspace?.admin_user ?? workspace?.user;
    return normalizeUser(raw);
  }, [workspace]);

  const refreshMember = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      member,
      loading,
      ready,
      failureReason,
      refreshMember,
      setMember: (payload) => normalizeUser(payload),
      clearProduct: clear,
    }),
    [member, loading, ready, failureReason, refreshMember, clear],
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function ProductProvider({ children }) {
  return (
    <ProductSessionProvider config={PRODUCT_API}>
      <ProductInner>{children}</ProductInner>
    </ProductSessionProvider>
  );
}

export function useProductMember() {
  const ctx = useContext(ProductContext);
  if (!ctx) {
    throw new Error('useProductMember must be used within ProductProvider');
  }
  return ctx;
}
